import React, {
    useEffect,
    useState,
    useContext,
    useCallback,
    useRef,
} from 'react';
import {
    BudgetProposalT,
    BudgetProposalSettingsT,
    FieldSettingsT,
} from 'routes/BudgetModule/config/types';
import 'routes/BudgetModule/components/BudgetProposal/shared/Grids/_commonGrid.scss';
import { useTranslation } from 'react-i18next';
import { BUDGET, BudgetExpenseOrIncome } from 'business/constants';
import Loading from 'components/Loading';
import {
    getImputationDimensionsLabelsAndCodes,
    EXCLUDED_DIMENSIONS_BY_CONTEXT,
} from 'routes/BudgetModule/config/hooks';
import { isActiveBudgetProposal } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import {
    ACTIONS,
    TRACKABLE_EVENTS,
    trackEventBudget,
    useGetAgGridTracking,
} from 'helpers/trackerHelpers';
import { FEATURE_ORPHAN_INSCRIPTION, isEnabled } from 'helpers/features';
import {
    GridApi,
    GridReadyEvent,
    PaginationChangedEvent,
    RowGroupOpenedEvent,
} from 'ag-grid-community';
import {
    InscriptionT,
    isOrphan,
} from 'routes/BudgetModule/shared/entities/inscription';
import { useLocation } from 'react-router-dom';
import { INSCRIPTION_ID_URL_PARAM } from 'routes/BudgetModule/config/constants';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import { applyInscriptionsFilter } from '../gridHelpers/helpers';
import { applyImputationsFilters } from './helpers/helpers';
import { TabActionsImputations } from './components/TabActions';
import ImputationsGrid from './components/ImputationsGrid';
import OrphanInscriptionsWarning from './components/OrphanInscriptionsWarning';
import OrphanInscriptions from './OrphanInscriptions/OrphanInscriptions';
import StyGridHeader from '../components/GridHeader';
import { useFindAndOpenInscriptionGrid } from './hooks/gridHelper';
import { exportBudgetLines } from './helpers/exportImputationsHelpers';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import { useGetImputationsColumns } from './useGetImputationsColumns';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { useGetEditableValuesByBudgetResource } from 'routes/BudgetModule/helpers/hooks/writeScopes';
import { useGetBudgetProposalEdition } from '../hooks/budgetProposalEdition';
import { ExpenseIncomeImputationDataT } from '../hooks/expenseIncomeImputations';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { useGetPhases } from 'routes/BudgetModule/shared/phases/hook';

type Props = {
    budgetProposal: BudgetProposalT;
    budgetProposals: BudgetProposalT[];
    expenseOrIncome: BudgetExpenseOrIncome;
    imputationsData: ExpenseIncomeImputationDataT[];
    inscriptionsData: { columns: ResponseColumnT[]; data: InscriptionT[] };
    settings: BudgetProposalSettingsT | undefined;
    isLoading: boolean;
    onSettingsEdit: (arg1: Partial<BudgetProposalSettingsT>) => void;
    resourceSoftwareId: string;
    resourceName: string | null;
    columnDetails: ResponseColumnT[];
    inscriptionsQueryCacheKey: InscriptionQueryKey;
    setHasOrphanInscriptions: DispatchState<boolean>;
};

export default function Imputations({
    budgetProposal,
    budgetProposals,
    expenseOrIncome,
    inscriptionsData,
    imputationsData,
    isLoading,
    settings,
    onSettingsEdit,
    resourceName,
    resourceSoftwareId,
    columnDetails,
    inscriptionsQueryCacheKey,
    setHasOrphanInscriptions,
}: Props): React.ReactElement {
    const { phase, name } = budgetProposal;
    const statusesSettings = useContext(StatusSettingsContext);
    const { data: phasesSettings = [] } = useGetPhases();
    const { t } = useTranslation();
    const [showEmptyBudgetLines, setShowEmptyBudgetLines] = useState<boolean>(
        Boolean(settings?.showEmptyBudgetLines),
    );
    const [filterInscriptions, setFilterInscriptions] = useState<string>('');
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [showOrphanWarning, setShowOrphanWarning] = useState<boolean>(false);
    const orphanGridRef = useRef<HTMLDivElement | null>(null);
    const [rowPerPage, setRowPerPage] = useState<number>(0);

    // -------- TRACKING ---------
    const baseTrackingMetadata = {
        budgetProposal: name,
        phase: t(`budgetProposal.${phase}`),
        service: resourceName,
    };

    const {
        onSearch: onFilterChanged,
        onSort: onSortChanged,
        onGroup: trackGrouping,
    } = useGetAgGridTracking(
        TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome.imputations,
        baseTrackingMetadata,
    );

    const { data: budgetProposalEdition, isLoading: isPairLoading } =
        useGetBudgetProposalEdition(budgetProposal.id, resourceSoftwareId);

    const onRowGroupOpened = useCallback(
        (event: RowGroupOpenedEvent) =>
            trackEventBudget(
                TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome.imputations
                    .gridUnpack,
                {
                    ...baseTrackingMetadata,
                    action: event.expanded ? ACTIONS.open : ACTIONS.close,
                },
            ),
        [baseTrackingMetadata],
    );

    // -------- FILTERING --------
    const { projects } = useGetProjects({
        enabled: isActiveBudgetProposal(budgetProposal), // no need to fetch if no inscription displayed
    });

    // We maintain a local state of the hidden columns to improve the UX:
    // without this local state, there is a lag in the inputs, and when user
    // changes the visibility of several columns in a few seconds, the backend
    // tends to reset the visibility of some columns. Using a debounce and this local
    // state fix this issue.
    // This means that the backend state is only used for initialization, and is then synced
    // with the frontend state.
    const [columnsSettings, setColumnsSettings] = useState<FieldSettingsT[]>(
        settings?.expenseGrid.columns ?? [],
    );

    // -------- FILTERING --------
    const inscriptions: InscriptionT[] = filterInscriptions
        ? applyInscriptionsFilter(
              inscriptionsData.data,
              filterInscriptions,
              projects,
          )
        : inscriptionsData.data;

    const data = applyImputationsFilters(
        imputationsData,
        inscriptions,
        budgetProposal,
        columnsSettings,
        columnDetails,
        !showEmptyBudgetLines,
        filterInscriptions,
        statusesSettings,
        phasesSettings,
    );

    // We maintain a local state of the hidden columns to improve the UX:
    // without this local state, there is a lag in the inputs, and when user
    // changes the visibility of several columns in a few seconds, the backend
    // tends to reset the visibility of some columns. Using a debounce and this local
    // state fix this issue.
    // This means that the backend state is only used for initialization, and is then synced
    // with the frontend state.

    const imputationRawDimensions = useContext(ImputationDimensionsContext);
    const imputationDimensions = getImputationDimensionsLabelsAndCodes(
        imputationRawDimensions,
        [
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.BudgetProposal,
            ...EXCLUDED_DIMENSIONS_BY_CONTEXT.ExpenseIncome,
        ],
    );

    const shownColumns = useGetImputationsColumns({
        budgetProposal,
        gridApi,
        columnsSettings,
    });

    // we refresh cells when inscriptions are updated, so the aggregation status on the imputation row is updated
    useEffect(() => {
        if (!gridApi) return;
        gridApi.refreshCells();
    }, [gridApi, JSON.stringify(inscriptions)]);

    useEffect(() => {
        // Race condition: when call to fetch settings is finished after this component is rendered,
        // useState(settings.expenseGrid.columns) is called when settings still has its initial state
        if (columnsSettings.length === 0) {
            setColumnsSettings(settings?.expenseGrid.columns ?? []);
        }
    }, [settings, imputationDimensions]);

    useEffect(() => {
        // This useEffect is used to expand nodes when we use
        // the search bar that match some inscriptions
        if (!gridApi) return;
        if (!filterInscriptions) {
            gridApi.collapseAll();
            return;
        }
        gridApi.forEachNode((node) => {
            gridApi.setRowNodeExpanded(node, true);
        });
    }, [gridApi, filterInscriptions]);

    const onGridReady = (params: GridReadyEvent) => {
        if (params.api) setGridApi(params.api);
    };

    const {
        data: editableValuesByResource,
        isLoading: isEditableValuesLoading,
    } = useGetEditableValuesByBudgetResource();

    // necessary to stabilize ref and avoid unmounting the detailcellrenderer of ImputationsGrid when updating its comment or amount
    const memoizedEditableValuesByResource = React.useMemo(
        () => editableValuesByResource,
        [JSON.stringify(editableValuesByResource)],
    );
    const memoizedDataPair = React.useMemo(
        () =>
            budgetProposalEdition || {
                canAddInscription: false,
                canAddInscriptionInPhase: false,
                isEditionOpenToResource: false,
            },
        [JSON.stringify(budgetProposalEdition)],
    );

    const orphanInscriptions = inscriptionsData.data.filter((ins) =>
        isOrphan(ins),
    );

    // -------- ORPHAN AUTO SCROLL ALERT MANAGEMENT --------

    const onOrphanWarningClose = useCallback(
        () => setShowOrphanWarning(false),
        [],
    );
    const onOrphanWarningClick = useCallback(() => {
        orphanGridRef?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
        });
    }, []);
    useEffect(() => {
        if (!isLoading && !isEditableValuesLoading && !isPairLoading) {
            if (orphanInscriptions.length > 0) {
                setShowOrphanWarning(true);
                setHasOrphanInscriptions(true);
            } else {
                setShowOrphanWarning(false);
                setHasOrphanInscriptions(false);
            }
        }
    }, [
        isLoading,
        isEditableValuesLoading,
        isPairLoading,
        orphanInscriptions.length,
    ]);

    const isEnabledOrphanInscription = isEnabled(FEATURE_ORPHAN_INSCRIPTION);

    // We want to display orphans grid for every bind if it has some orphans
    const showOrphanGrid =
        !isLoading &&
        !isPairLoading &&
        !isEditableValuesLoading &&
        !isPairLoading &&
        (isEnabledOrphanInscription || orphanInscriptions.length > 0);

    // -------- OPENING INSCRIPTIONGRID FROM URLSEARCHPARAM inscription_id --------
    //
    // - expand budget_line row that holds target inscription and its parent (when grouped)
    // - move to the page ImputationGrid page where it is defined
    const onPaginationChanged = useCallback(
        (event: PaginationChangedEvent) => {
            const newPagination = event.api.paginationGetPageSize();
            if (newPagination !== rowPerPage) {
                setRowPerPage(newPagination);
            }
        },
        [rowPerPage],
    );

    const inscriptionIdParam = new URLSearchParams(useLocation().search).get(
        INSCRIPTION_ID_URL_PARAM,
    );
    useFindAndOpenInscriptionGrid(
        inscriptions,
        isLoading && isEditableValuesLoading && isPairLoading,
        gridApi,
        inscriptionIdParam,
        rowPerPage,
    );

    return (
        <>
            {showOrphanWarning && (
                <OrphanInscriptionsWarning
                    close={onOrphanWarningClose}
                    onClick={onOrphanWarningClick}
                />
            )}
            <FullScreenProvider module={BUDGET}>
                <StyGridHeader>
                    <TabActionsImputations
                        budgetProposal={budgetProposal}
                        inscriptionsData={inscriptionsData}
                        showEmptyBudgetLines={showEmptyBudgetLines}
                        setShowEmptyBudgetLines={setShowEmptyBudgetLines}
                        setFilterInscriptions={setFilterInscriptions}
                        onSettingsEdit={onSettingsEdit}
                        exportFunction={() =>
                            exportBudgetLines({
                                expenseOrIncome,
                                gridApi,
                            })
                        }
                    />
                </StyGridHeader>
                {isLoading || isEditableValuesLoading || isPairLoading ? (
                    <Loading />
                ) : (
                    <ImputationsGrid
                        budgetProposal={budgetProposal}
                        budgetProposals={budgetProposals}
                        expenseOrIncome={expenseOrIncome}
                        imputationsData={data}
                        inscriptions={inscriptions}
                        columns={shownColumns}
                        editableValuesByResource={
                            memoizedEditableValuesByResource
                        }
                        filterInscriptions={filterInscriptions}
                        onGridReady={onGridReady}
                        settings={settings}
                        onSettingsEdit={onSettingsEdit}
                        columnsSettings={columnsSettings}
                        setColumnsSettings={setColumnsSettings}
                        trackGrouping={trackGrouping}
                        onFilterChanged={onFilterChanged}
                        onSortChanged={onSortChanged}
                        onRowGroupOpened={onRowGroupOpened}
                        onPaginationChanged={onPaginationChanged}
                        inscriptionsQueryCacheKey={inscriptionsQueryCacheKey}
                    />
                )}
            </FullScreenProvider>

            {showOrphanGrid && (
                <div ref={orphanGridRef}>
                    <OrphanInscriptions
                        budgetProposal={budgetProposal}
                        budgetProposals={budgetProposals}
                        expenseOrIncome={expenseOrIncome}
                        resourceSoftwareId={resourceSoftwareId}
                        budgetProposalEdition={memoizedDataPair}
                        resourceName={resourceName!}
                        orphanInscriptions={orphanInscriptions}
                        inscriptionsQueryCacheKey={inscriptionsQueryCacheKey}
                    />
                </div>
            )}
        </>
    );
}
